import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useState } from "react";
import { useSettings } from "@/providers/SettingsProvider";
import { EditEventModal } from "./EditEventModal";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
	AlertDialog, 
	AlertDialogContent, 
	AlertDialogHeader, 
	AlertDialogTitle, 
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogAction,
	AlertDialogCancel
} from "@/components/ui/alert-dialog";
import { Clock, Calendar, FileText, Edit, Trash2, LinkIcon } from "lucide-react";
import { useRelatedEntities } from "@/hooks/useRelatedEntities";
import { RelatedEntityList } from "./RelatedEntityList";

// Function to detect if a string contains HTML
const isHtml = (str: string): boolean => {
	// Simple check for HTML tags
	return /<[a-z][\s\S]*>/i.test(str);
};

// Function to extract meaningful HTML content
const getVisibleHtmlContent = (rawHtml: string): string => {
	if (!rawHtml) return "";
	try {
		const parser = new window.DOMParser();
		const doc = parser.parseFromString(rawHtml, "text/html");
		// Remove unwanted tags
		["head", "meta", "style", "script"].forEach(tag => {
			doc.querySelectorAll(tag).forEach(el => el.remove());
		});
		// Remove empty divs, spans, and excessive <br>
		doc.querySelectorAll("div,span").forEach(el => {
			if (!el.textContent?.trim()) el.remove();
		});
		doc.querySelectorAll("br").forEach(el => el.remove());
		// Remove &nbsp; and trim
		let content = doc.body.innerHTML.replace(/&nbsp;/gi, "").trim();
		// Remove all whitespace and check if anything remains
		if (!content.replace(/\s/g, "")) return "";
		return content;
	} catch {
		return "";
	}
};

// Function to safely render HTML content, only if meaningful
const RenderHtmlOrText = ({ content }: { content: string }) => {
	if (isHtml(content)) {
		const cleaned = getVisibleHtmlContent(content);
		if (!cleaned) {
			return <div className="text-gray-400 italic">No details available.</div>;
		}
		return (
			<div 
				className="text-gray-600" 
				dangerouslySetInnerHTML={{ __html: cleaned }} 
			/>
		);
	}
	if (!content.trim()) {
		return <div className="text-gray-400 italic">No details available.</div>;
	}
	return <div className="text-gray-600 whitespace-pre-wrap">{content}</div>;
};

interface EventDetailsModalProps {
	event: {
		id: string;
		title: string;
		start: Date;
		end: Date;
		description?: string;
		color?: string;
		metadata?: {
			candidates?: string[];
			offers?: string[];
			companies?: string[];
			matches?: string[];
			contacts?: string[];
		};
	};
	isOpen: boolean;
	onClose: () => void;
	onUpdate?: (eventId: string, updatedEvent: { 
		title: string; 
		start: Date; 
		end: Date; 
		description?: string;
		color?: string;
		relations?: {
			candidates: string[];
			offers: string[];
			companies: string[];
			matches: string[];
			contacts: string[];
		};
	}) => void;
	onDelete?: (id: string) => void;
}

export const EventDetailsModal = ({ event, isOpen, onClose, onUpdate, onDelete }: EventDetailsModalProps) => {
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const { timeFormat, timezone } = useSettings();
	const is24Hour = timeFormat === 'HH-mm' || timeFormat === 'HH-mm-ss';
	const userTimezone = timezone || 'UTC';

	// Initialize empty arrays for metadata properties that might be undefined
	const metadata = event?.metadata || {};
	const relations = {
		candidates: metadata.candidates || [],
		offers: metadata.offers || [],
		companies: metadata.companies || [],
		matches: metadata.matches || [],
		contacts: metadata.contacts || []
	};

	// Get actual entity data based on IDs
	const { entities, isLoading: loadingEntities } = useRelatedEntities(relations);

	const handleEdit = () => {
		setShowEditModal(true);
	};

	const handleDelete = () => {
		setShowDeleteConfirm(true);
	};

	const confirmDelete = () => {
		if (onDelete) {
			// First close the confirmation dialog
			setShowDeleteConfirm(false);
			
			// Then close the main dialog
			onClose();
			
			// Use a more substantial timeout to ensure DOM cleanup
			setTimeout(() => {
				onDelete(event.id);
			}, 200);
		}
	};

	const handleUpdate = (eventId: string, updatedEvent: { 
		title: string; 
		start: Date; 
		end: Date; 
		description?: string;
		color?: string;
		relations?: {
			candidates: string[];
			offers: string[];
			companies: string[];
			matches: string[];
			contacts: string[];
		};
	}) => {
		if (onUpdate) {
			setShowEditModal(false);
			onUpdate(eventId, updatedEvent);
		}
	};

	// Handler for AlertDialog close
	const handleDeleteDialogClose = (open: boolean) => {
		// Only handle closing, not opening to prevent state conflicts
		if (!open) {
			setShowDeleteConfirm(false);
		}
	};

	// Handler for main Dialog close
	const handleMainDialogClose = (open: boolean) => {
		// Only close the main dialog if the delete confirmation isn't showing
		if (!open && !showDeleteConfirm) {
			onClose();
		}
	};

	// Helper to check if there are any linked items
	const hasLinkedItems = Object.values(relations).some(arr => arr.length > 0);

	return (
		<>
			<Dialog open={isOpen && !showEditModal} onOpenChange={handleMainDialogClose}>
				<DialogContent className="max-w-[900px] w-[90vw] mx-auto max-h-[80vh]">
					<DialogHeader>
						<DialogTitle className="text-xl">{event.title}</DialogTitle>
					</DialogHeader>
					
					<ScrollArea className="max-h-[calc(80vh-180px)]">
						<div className="space-y-4 py-2">
							<div className="flex items-start gap-2">
								<Calendar className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
								<div>
									<p className="font-medium">
										{formatInTimeZone(event.start, userTimezone, 'EEEE, MMMM d, yyyy')}
									</p>
								</div>
							</div>
							
							<div className="flex items-start gap-2">
								<Clock className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
								<div>
									<p className="font-medium">
										{formatInTimeZone(event.start, userTimezone, is24Hour ? 'HH:mm' : 'h:mm a')} - 
										{formatInTimeZone(event.end, userTimezone, is24Hour ? 'HH:mm' : 'h:mm a')}
									</p>
								</div>
							</div>
							
							{event.description && (
								<div className="flex items-start gap-2">
									<FileText className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
									<RenderHtmlOrText content={event.description} />
								</div>
							)}

							{/* Display linked items with actual names */}
							{hasLinkedItems && (
								<div className="flex items-start gap-2">
									<LinkIcon className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
									<div>
										<p className="font-medium mb-2">Related Items</p>
										<div className="space-y-3">
											{loadingEntities ? (
												<div className="animate-pulse h-4 bg-gray-200 rounded w-2/3"></div>
											) : (
												<>
													{entities.candidates.length > 0 && (
														<RelatedEntityList 
															entities={entities.candidates} 
															entityType="candidates" 
														/>
													)}
													
													{entities.offers.length > 0 && (
														<RelatedEntityList 
															entities={entities.offers} 
															entityType="offers" 
														/>
													)}
													
													{entities.companies.length > 0 && (
														<RelatedEntityList 
															entities={entities.companies} 
															entityType="companies" 
														/>
													)}
													
													{entities.matches.length > 0 && (
														<RelatedEntityList 
															entities={entities.matches} 
															entityType="matches" 
														/>
													)}
													
													{entities.contacts.length > 0 && (
														<RelatedEntityList 
															entities={entities.contacts} 
															entityType="contacts" 
														/>
													)}
												</>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					</ScrollArea>
					
					<DialogFooter className="flex justify-between sm:justify-end gap-2">
						{onDelete && (
							<Button variant="destructive" onClick={handleDelete}>
								<Trash2 className="h-4 w-4 mr-1" />
								Delete
							</Button>
						)}
						{onUpdate && (
							<Button onClick={handleEdit}>
								<Edit className="h-4 w-4 mr-1" />
								Edit
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>
			
			{/* Edit Modal */}
			{onUpdate && (
				<EditEventModal
					isOpen={showEditModal}
					onClose={() => setShowEditModal(false)}
					onSave={handleUpdate}
					onDelete={onDelete || (() => {})}
					event={{
						...event,
						metadata: event.metadata || {
							candidates: [],
							offers: [],
							companies: [],
							matches: [],
							contacts: []
						}
					}}
				/>
			)}
			
			{/* Delete Confirmation */}
			<AlertDialog 
				open={showDeleteConfirm} 
				onOpenChange={handleDeleteDialogClose}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Are you sure?</AlertDialogTitle>
						<AlertDialogDescription>
							This will permanently delete the event "{event.title}". This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction 
							onClick={confirmDelete} 
							className="bg-red-600 hover:bg-red-700"
						>
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};
